import { memo, useCallback, useEffect, useLayoutEffect, useRef, useSyncExternalStore, type MouseEvent, type PointerEvent, type ReactElement, type RefObject } from "react";
import { TOOL_BELT_SLOTS, type Rules } from "odyssey-shared";
import { pageKeyHints } from "../touch/key-hints.ts";
import { ArtIcon, ITEM_SLOT_ART_BOX, SmallNumber, glyphClass, logical } from "../../ui/kit/index.ts";
import { fitsSmallDigits } from "../../ui/kit/small-digits.ts";
import { say } from "../../words.ts";
import { itemGlyph } from "../bag/describe.ts";
import { ABILITY_SLOT } from "../bar/layout.ts";
import { useKeyLabel } from "../controls/use-key.ts";
import { DragGhost } from "../drag/ghost.tsx";
import { dropValue } from "../drag/targets.ts";
import { slotRect } from "../hud/area-slots.ts";
import { toolButtonRect } from "../hud/touch-slots.ts";
import { TOOL_BELT, type HudArea } from "../hud/slots.ts";
import { itemArt, useIcons, type IconsView } from "../icons/context.tsx";
import { fanCloses } from "./dismiss.ts";
import { useBeltDrag } from "./drag.ts";
import { useToolKeys } from "./keys.ts";
import type { BeltNotice, BeltSnapshot, ToolBelt } from "./store.ts";

/**
 * The tool belt (ADR-142 decision 3b and its notes of 2026-10-29 and 2026-10-31; the mockup `tool-belt-mockup`'s chip):
 * one chip of a bar slot's size showing the tool in hand, on every keyboard page. A press on it fans the four slots in a
 * column above it, a bar slot's size each and a pixel apart, each with its key at its top left where it has one. A press
 * on a slot takes its tool out, or puts it away when it is the one in hand, and closes the fan; an empty slot is not a
 * pick and leaves the fan open. A press on the chip, Escape or a click outside (`dismiss.ts`) closes it too. A tool
 * dragged from the bag goes onto a slot of the open fan, or onto the chip and so on the first empty slot
 * (`bag/drag.ts`); with a mouse a drag from the fan swaps two slots or takes the tool off (`drag.ts`). A line above the
 * chip says for a moment what was taken out or put away, and "Only tools go on the belt." over a slot that refuses what
 * is carried. On a touch page the cluster's Tool button is the chip and the fan a row of 32 px slots to its left. The
 * belt is the only way to hold a tool.
 */

/**
 * The item name of a rules id.
 *
 * @param rules The rules.
 * @param item The item's rules id.
 * @returns Its name, or the id for an item the rules lack.
 */
function itemName(rules: Rules, item: string): string {
  return rules.items.get(item)?.name ?? item;
}

/**
 * The line a notice says.
 *
 * @param rules The rules the tool is named by.
 * @param notice The notice.
 * @param key The key that cycles tools.
 * @returns The words.
 */
function noticeLine(rules: Rules, notice: BeltNotice, key: string): string {
  if (notice.kind === "onlyTools" || notice.item === null) {
    return say("belt.onlyTools");
  }
  const tool = itemName(rules, notice.item);
  if (!pageKeyHints.getSnapshot()) {
    return say(notice.kind === "inHand" ? "belt.inHandTouch" : "belt.putAwayTouch", { tool });
  }
  return say(notice.kind === "inHand" ? "belt.inHand" : "belt.putAway", { tool, key });
}

/**
 * Keeps a pointer press on a slot from taking focus, so a slot left focused never answers Space or Enter, which the game
 * captures, as a bar slot does.
 *
 * @param event The press.
 */
function keepFocus(event: MouseEvent<HTMLElement>): void {
  event.preventDefault();
}

/**
 * The line above the belt: what a notice says, or "Only tools go on the belt." while a drag is held over a slot that
 * would refuse what it carries.
 *
 * @param props The `snapshot`, the `rules`, the key that cycles tools, a `hint` that stands in place of the rest, and whether
 *   the line stands `aside`, left of the slot, where the chip's fan covers the place above it.
 * @returns The line, or nothing.
 */
function BeltLine({ snapshot, rules, cycle, hint, aside = false }: { readonly snapshot: BeltSnapshot; readonly rules: Rules; readonly cycle: string; readonly hint: string | null; readonly aside?: boolean }): ReactElement | null {
  const refusing = snapshot.over?.tone === "danger";
  const text = hint ?? (refusing ? say("belt.onlyTools") : snapshot.notice === null ? null : noticeLine(rules, snapshot.notice, cycle));
  return text === null ? null : (
    <div className={aside ? "ot-hud-belt__line is-aside" : "ot-hud-belt__line"} data-hud="tool-line" role="status">
      {text}
    </div>
  );
}

/**
 * One slot of the fan above the chip: a bar slot's size, the tool's art or glyph, the selected ring while in hand, the drop ring
 * while a drag is held over it, its key at the top left where it has one.
 *
 * @param props The slot's `index`, its `item`, the `rules`, the `icons`, whether it is `selected` or `dragging` (the slot a
 *   drag from the fan carries), the drop mark over it (`tone`), its key label (`keyLabel`), the press handler for a drag and
 *   what a click does.
 * @returns The slot.
 */
function FanSlot({ index, size, item, rules, icons, selected, dragging, tone, keyLabel, onPointerDown, onTap }: { readonly index: number; readonly size: number; readonly item: string | null; readonly rules: Rules; readonly icons: IconsView; readonly selected: boolean; readonly dragging: boolean; readonly tone: "positive" | "danger" | null; readonly keyLabel: string | null; readonly onPointerDown: (event: PointerEvent<HTMLElement>) => void; readonly onTap: () => void }): ReactElement {
  const art = item === null ? null : itemArt(icons, rules, item, ABILITY_SLOT.size);
  const glyph = item === null ? null : itemGlyph(rules, item);
  const label = item === null ? say("belt.slot", { slot: String(index + 1) }) : say(selected ? "belt.slotSelected" : "belt.slotNamed", { slot: String(index + 1), tool: itemName(rules, item) });
  const room = size - (size === ABILITY_SLOT.size ? 2 : 4);
  const classes = ["ot-hud-ability", "ot-hud-belt__fan-slot", item === null ? "is-empty" : "is-ready"];
  if (selected || dragging) {
    classes.push("is-selected");
  }
  if (tone !== null) {
    classes.push(tone === "positive" ? "is-drop-target" : "is-drop-refused");
  }
  return (
    <button type="button" className={classes.join(" ")} data-hud={`tool-${index + 1}`} data-drop={dropValue({ surface: "belt", index })} data-selected={selected ? "true" : undefined} aria-label={label} aria-pressed={item === null ? undefined : selected} aria-disabled={item === null} onMouseDown={keepFocus} onPointerDown={onPointerDown} onClick={onTap} style={{ width: logical(size), height: logical(size) }}>
      {art !== null ? <ArtIcon art={art} room={room} at={{ left: 0, top: 0 }} /> : glyph === null ? null : <span className={`ot-hud-ability__icon ot-icon ${glyphClass(glyph)}`} aria-hidden="true" style={{ left: logical((room - ABILITY_SLOT.icon) / 2), top: logical((room - ABILITY_SLOT.icon) / 2) }} />}
      {keyLabel === null ? null : fitsSmallDigits(keyLabel) ? (
        <SmallNumber text={keyLabel} className="ot-hud-ability__key" place={{ left: 0, top: 0 }} />
      ) : (
        <span className="ot-hud-ability__key" aria-hidden="true">
          {keyLabel}
        </span>
      )}
    </button>
  );
}

/**
 * Closes the fan on Escape, heard before the game's own keys, and on a click outside the belt; a click on the cluster's
 * Tool button is its own press, which has already toggled the fan.
 *
 * @param open Whether the fan is open.
 * @param root The belt's element.
 * @param close Closes the fan.
 */
function useFanDismiss(open: boolean, root: RefObject<HTMLDivElement | null>, close: () => void): void {
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    /**
     * Closes the fan on Escape and keeps the key from reaching anything else.
     *
     * @param event The key.
     */
    const keyed = (event: KeyboardEvent): void => {
      if (fanCloses({ kind: "key", key: event.key })) {
        event.preventDefault();
        event.stopImmediatePropagation();
        close();
      }
    };
    /**
     * Where an event landed: inside the belt, or on the cluster's Tool button.
     *
     * @param event The event.
     * @returns The two facts `fanCloses` asks.
     */
    const landed = (event: globalThis.MouseEvent | globalThis.PointerEvent): { readonly insideBelt: boolean; readonly onToolButton: boolean } => {
      const target = event.target;
      return { insideBelt: root.current !== null && target instanceof Node && root.current.contains(target), onToolButton: target instanceof Element && target.closest('[data-touch="tool"]') !== null };
    };
    /**
     * Closes the fan on a click that lands outside the belt.
     *
     * @param event The click.
     */
    const clicked = (event: globalThis.MouseEvent): void => {
      if (fanCloses({ kind: "click", ...landed(event) })) {
        close();
      }
    };
    window.addEventListener("keydown", keyed, { capture: true });
    window.addEventListener("click", clicked, { capture: true });
    /**
     * A touch's press outside closes the fan at once, since a tap on the game's canvas raises no click.
     *
     * @param event The press.
     */
    const touched = (event: globalThis.PointerEvent): void => {
      if (fanCloses({ kind: "press", pointerType: event.pointerType, ...landed(event) })) {
        close();
      }
    };
    window.addEventListener("pointerdown", touched, { capture: true });
    return () => {
      window.removeEventListener("pointerdown", touched, { capture: true });
      window.removeEventListener("keydown", keyed, { capture: true });
      window.removeEventListener("click", clicked, { capture: true });
    };
  }, [open, root, close]);
}

/**
 * The tool belt in its slot: on a keyboard page the chip, one square of a bar slot's size showing the tool in hand, with the
 * fan of four slots above it; on a touch page the cluster's Tool button is the chip and the fan of four 32 px slots opens
 * in a row to its left. The fan is the belt's `fan` state, so the Tool button, the chip, Escape and a click outside all reach
 * it.
 *
 * @param props The `area`, the `belt`, the `rules` and whether the fan's slots draw their keys (`showKey`).
 * @returns The belt.
 */
function Chip({ area, belt, rules, showKey }: { readonly area: HudArea; readonly belt: ToolBelt; readonly rules: Rules; readonly showKey: boolean }): ReactElement {
  const snapshot = useSyncExternalStore(belt.subscribe, belt.getSnapshot, belt.getSnapshot);
  const icons = useIcons();
  const drag = useBeltDrag(belt);
  const keys = useToolKeys();
  const cycle = useKeyLabel("toolCycle");
  const root = useRef<HTMLDivElement>(null);
  const touch = area.touch === true;
  const open = snapshot.fan;
  const close = useCallback(() => belt.setFan(false), [belt]);
  useFanDismiss(open, root, close);
  const rect = touch ? toolButtonRect(area) : slotRect("tools", area);
  const size = touch ? TOOL_BELT.touchFan : TOOL_BELT.fan;
  const { record } = snapshot;
  const held = record.selected === 0 ? null : (record.slots[record.selected - 1] ?? null);
  const dragged = snapshot.dragging === null ? null : (record.slots[snapshot.dragging] ?? null);
  const art = held === null ? null : itemArt(icons, rules, held, ABILITY_SLOT.size);
  const glyph = held === null ? null : itemGlyph(rules, held);
  const tone = snapshot.over?.index === TOOL_BELT_SLOTS ? snapshot.over.tone : null;
  const keyboard = pageKeyHints.getSnapshot();
  useLayoutEffect(() => {
    if (snapshot.dragging !== null) {
      drag.placeNow();
    }
  }, [snapshot.dragging, drag]);
  const classes = ["ot-hud-ability", "ot-hud-belt__chip", held === null ? "is-empty" : "is-ready"];
  if (held !== null) {
    classes.push("is-selected");
  }
  if (tone !== null) {
    classes.push(tone === "positive" ? "is-drop-target" : "is-drop-refused");
  }
  const hint = open ? (keyboard ? null : say("belt.fanHint")) : snapshot.over !== null && snapshot.over.tone === "positive" && !keyboard ? say("belt.dragHint") : null;
  const frame = TOOL_BELT.fanPad + TOOL_BELT.fanEdge;
  const span = size + 2 * frame;
  const placing = touch ? { right: logical(rect.width + TOOL_BELT.fanPad), bottom: logical((rect.height - span) / 2), flexDirection: "row" as const, columnGap: logical(TOOL_BELT.fanGap) } : { bottom: logical(rect.height + TOOL_BELT.fanPad), left: logical(-frame), rowGap: logical(TOOL_BELT.fanGap) };
  return (
    <div ref={root} className={`ot-hud-slot ot-hud-belt${touch ? " is-touch" : " is-chip"}${open ? " is-raised" : ""}`} data-hud-slot="tools" data-hud="tool-belt" data-selected={record.selected} role="group" aria-label={say("belt.title")} style={{ left: logical(rect.left), top: logical(rect.top), width: logical(rect.width), height: logical(rect.height) }}>
      {touch ? null : (
        <>
          {open || !showKey ? null : (
            <span className="ot-hud-belt__tab" aria-hidden="true">
              {say("belt.chip")}
            </span>
          )}
          <button type="button" className={classes.join(" ")} data-hud="tool-chip" data-drop={dropValue({ surface: "belt", index: TOOL_BELT_SLOTS })} aria-expanded={open} aria-label={held === null ? say("belt.chipEmpty") : say("belt.chipHolding", { tool: itemName(rules, held) })} onMouseDown={keepFocus} onClick={() => belt.toggleFan()} style={{ width: logical(TOOL_BELT.chip), height: logical(TOOL_BELT.chip) }}>
            {art !== null ? <ArtIcon art={art} room={ABILITY_SLOT.artWell} at={{ left: 0, top: 0 }} /> : glyph === null ? null : <span className={`ot-hud-ability__icon ot-icon ${glyphClass(glyph)}`} aria-hidden="true" style={{ left: logical((ABILITY_SLOT.size - ABILITY_SLOT.icon) / 2 - 1), top: logical((ABILITY_SLOT.size - ABILITY_SLOT.icon) / 2 - 1) }} />}
          </button>
        </>
      )}
      {open ? (
        <div className="ot-hud-belt__fan" data-hud="tool-fan" role="group" aria-label={say("belt.title")} style={{ ...placing, padding: logical(TOOL_BELT.fanPad), borderWidth: logical(TOOL_BELT.fanEdge) }}>
          {record.slots.map((item, index) => (
            <FanSlot
              key={index}
              index={index}
              size={size}
              item={item}
              rules={rules}
              icons={icons}
              selected={record.selected === index + 1 && item !== null}
              dragging={snapshot.dragging === index}
              tone={snapshot.over?.index === index ? snapshot.over.tone : null}
              keyLabel={showKey && !touch ? (keys[index] ?? null) : null}
              onPointerDown={drag.onPointerDown}
              onTap={() => {
                if (drag.swallow()) {
                  return;
                }
                belt.toggle(index);
                if (item !== null) {
                  belt.setFan(false);
                }
              }}
            />
          ))}
        </div>
      ) : null}
      <BeltLine snapshot={snapshot} rules={rules} cycle={cycle} hint={hint} aside={open && !touch} />
      {dragged === null ? null : <DragGhost host={drag.ghost} label={itemName(rules, dragged)} rarity={rules.items.get(dragged)?.rarity ?? null} art={itemArt(icons, rules, dragged, ITEM_SLOT_ART_BOX)} glyph={itemGlyph(rules, dragged)} tone={snapshot.over?.tone ?? null} />}
    </div>
  );
}

/**
 * The tool belt in its slot as the area stands it.
 *
 * @param props The `area` it is laid out on, the page's `belt`, the `rules` its tools are named by, whether the fan's slots
 *   draw their keys (`showKey`, the viewer's Show key numbers on the bar) and `onCommit`, told after each commit.
 * @returns The belt.
 */
export const ToolBeltView = memo(function ToolBeltView({ area, belt, rules, showKey, onCommit }: { readonly area: HudArea; readonly belt: ToolBelt; readonly rules: Rules; readonly showKey: boolean; readonly onCommit: (() => void) | undefined }): ReactElement | null {
  useEffect(() => {
    onCommit?.();
  });
  const arrived = useSyncExternalStore(belt.subscribe, () => belt.getSnapshot().arrived, () => belt.getSnapshot().arrived);
  return arrived ? <Chip area={area} belt={belt} rules={rules} showKey={showKey} /> : null;
});
